import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, Users, RefreshCw, Download, Search, Filter,
  ArrowLeft, TrendingUp, TrendingDown, Wallet, AlertCircle,
  CheckCircle, XCircle, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useReportPagination } from '@/components/ReportPagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CautionMoneyReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const studentWise = report?.student_wise || [];
  const { paginatedData: pageStudents, PaginationControls } = useReportPagination(studentWise);
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: ''
  });
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [filters]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);

      const response = await fetch(`${API_URL}/api/caution-money/report?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!report?.student_wise) return;

    const headers = ['Student Name', 'Roll No', 'Total Paid', 'Refunded', 'Forfeited', 'Balance', 'Status'];
    const rows = report.student_wise.map(s => [
      s.student_name,
      s.student_roll,
      s.total_paid,
      s.total_refunded,
      s.total_forfeited,
      s.current_balance,
      s.status
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `caution_money_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusIcon = (status) => {
    if (status === 'Held') return <Wallet className="h-4 w-4 text-blue-500" />;
    if (status === 'Fully Refunded') return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (status === 'Forfeited') return <XCircle className="h-4 w-4 text-red-500" />;
    return <AlertCircle className="h-4 w-4 text-gray-500" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Caution Money Report</h1>
            <p className="text-gray-600 mt-1">Track all security deposits</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReport}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {report?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">₹{report.summary.total_collected?.toLocaleString('en-IN')}</p>
                  <p className="text-sm text-gray-500">Total Collected</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingDown className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">₹{report.summary.total_refunded?.toLocaleString('en-IN')}</p>
                  <p className="text-sm text-gray-500">Total Refunded</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">₹{report.summary.total_forfeited?.toLocaleString('en-IN')}</p>
                  <p className="text-sm text-gray-500">Total Forfeited</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Wallet className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">₹{report.summary.outstanding_balance?.toLocaleString('en-IN')}</p>
                  <p className="text-sm text-gray-500">Outstanding (Liability)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">All</option>
                <option value="held">Held (Active)</option>
                <option value="refunded">Refunded</option>
                <option value="forfeited">Forfeited</option>
              </select>
            </div>

            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <Button 
              variant="outline" 
              onClick={() => setFilters({ status: '', startDate: '', endDate: '' })}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Student-wise Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Student-wise Summary
            <span className="text-sm font-normal text-gray-500">
              ({report?.student_wise?.length || 0} students)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : report?.student_wise?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No caution money records found
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Student</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Paid</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Refunded</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Forfeited</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Balance</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageStudents.map((student) => (
                    <tr key={student.student_id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <p className="font-medium">{student.student_name}</p>
                        <p className="text-sm text-gray-500">{student.student_roll}</p>
                      </td>
                      <td className="p-3 text-right font-medium text-blue-600">
                        ₹{student.total_paid?.toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-right text-green-600">
                        ₹{student.total_refunded?.toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-right text-red-600">
                        ₹{student.total_forfeited?.toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-right font-semibold">
                        ₹{student.current_balance?.toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          student.status === 'Held' ? 'bg-blue-100 text-blue-700' :
                          student.status === 'Fully Refunded' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {getStatusIcon(student.status)}
                          {student.status}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        {student.current_balance > 0 && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/fees/caution-money/refund?student=${student.student_id}`)}
                          >
                            Refund
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls />
            </>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Button onClick={() => navigate('/fees/caution-money/pay')} className="bg-green-600 hover:bg-green-700">
          <DollarSign className="h-4 w-4 mr-2" /> Collect New
        </Button>
        <Button variant="outline" onClick={() => navigate('/fees/caution-money/refund')}>
          <TrendingDown className="h-4 w-4 mr-2" /> Process Refund
        </Button>
        <Button 
          variant="outline" 
          onClick={() => navigate('/fees/caution-money/due')}
          className="border-red-300 text-red-600 hover:bg-red-50"
        >
          <AlertCircle className="h-4 w-4 mr-2" /> View Due Report
        </Button>
      </div>
    </div>
  );
}
